import { useEffect, useContext, useCallback, useRef } from 'react'
import { useToaster } from '@enterprise-ui/canvas-ui-react'
import {
  Button,
  Grid,
  Layout,
  Spinner,
  Card,
} from '@enterprise-ui/canvas-ui-react'
import { CostChangeInsightsPageContext } from './pageState'
import { VendorContext } from '../../globalState/Vendor/index'
import { CollapsibleItemGrouping } from './CollapsibleItemGrouping'
import {
  COST_CHANGE_INSIGHTS_ITEM_OPTIONS,
  COST_CHANGE_INSIGHTS_LOCATION_OPTIONS,
  COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
  COST_CHANGE_INSIGHTS_ITEM_RESULTS,
  TOASTER_DEFAULTS,
  convertDateToLocalDateStringWithBackendFormatting,
} from '../../globalConfig/constants'
import { TablePagination } from '../../globalComponents/TablePagination'
import { getItemCostChangeHistory } from '../../services/requests'
import { generateCostLocationGroupsArray } from './helperFunctions'

export const CostChangeInsightsResults = () => {
  const [pageState, pageDispatch] = useContext(CostChangeInsightsPageContext)
  const [vendorState] = useContext(VendorContext)
  const scrollRef = useRef(null)
  const makeToast = useToaster()
  const dvsVendorState = vendorState.dvsStatus

  const headerFields = [
    COST_CHANGE_INSIGHTS_ITEM_RESULTS.DPCI.label,
    COST_CHANGE_INSIGHTS_ITEM_RESULTS.TCIN.label,
    COST_CHANGE_INSIGHTS_ITEM_RESULTS.ITEM_NAME.label,
  ]

  const isAtleastOneItemGroupExpanded = Object.keys(
    pageState.itemExpandableStates,
  ).some((key) => Boolean(pageState.itemExpandableStates[key]?.isExpanded))

  const sendFailureToast = (tcin) => {
    makeToast({
      ...TOASTER_DEFAULTS,
      type: 'error',
      heading: 'Error',
      message:
        'Failed to get submission records for  the following TCIN: ' + tcin,
    })
  }

  const makeItemCostChangeHistoryRequest = (tcin, filters) => {
    pageDispatch({
      type: 'update_submission_results',
      item: tcin,
      value: { isLoading: true },
    })
    const requestBody = {
      ...filters,
    }
    Object.values(COST_CHANGE_INSIGHTS_ITEM_OPTIONS).forEach((field) => {
      delete requestBody[field.variableName]
    })
    requestBody.vendorIds = [vendorState.selectedVendor]
    requestBody.tcin = tcin
    requestBody.costLocationGroups = generateCostLocationGroupsArray(
      filters,
      dvsVendorState,
      vendorState.selectedVendor,
    )
    delete requestBody[
      COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE
        .variableName
    ]
    delete requestBody[
      COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES
        .variableName
    ]
    Object.values(COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS).forEach((option) => {
      if (requestBody[option.variableName]) {
        requestBody[option.variableName] = {
          startDate: convertDateToLocalDateStringWithBackendFormatting(
            new Date(requestBody[option.variableName].startDate),
          ),
          endDate: convertDateToLocalDateStringWithBackendFormatting(
            new Date(requestBody[option.variableName].endDate),
          ),
        }
      }
    })

    getItemCostChangeHistory(requestBody)
      .then((response) => {
        if (response.status === 200) {
          pageDispatch({
            type: 'update_submission_results',
            item: tcin,
            value: { costChangeSubmissionHistory: response.data },
          })
        } else {
          pageDispatch({
            type: 'update_submission_results',
            item: tcin,
            value: { isLoading: false },
          })
          sendFailureToast(tcin)
        }
      })
      .catch(() => {
        pageDispatch({
          type: 'update_submission_results',
          item: tcin,
          value: { isLoading: false },
        })
        sendFailureToast(tcin)
      })
  }

  useEffect(() => {
    if (Object.keys(pageState.itemExpandableStates)) {
      Object.keys(pageState.itemExpandableStates).forEach((key) => {
        const hasSubmissionResults = Boolean(pageState.submissionResults[key])
        if (!hasSubmissionResults) {
          makeItemCostChangeHistoryRequest(
            key,
            pageState.costChangeItemsRequestCriteria,
          )
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.itemExpandableStates])

  useEffect(() => {
    if (pageState.isGetCostChangeItemsLoading) {
      scrollRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [pageState.isGetCostChangeItemsLoading])

  const onExpand = (tcin) => {
    pageDispatch({ type: 'flip_item_expandable_status', value: tcin })
  }
  const updatePage = (tcin, pageNum) => {
    pageDispatch({
      type: 'update_item_expandable_page',
      item: tcin,
      value: pageNum,
    })
  }
  const updateRecordsPerPage = (tcin, perPageNum) => {
    pageDispatch({
      type: 'update_item_expandable_records_per_page',
      item: tcin,
      value: perPageNum,
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedOnExpand = useCallback(onExpand, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedUpdatePage = useCallback(updatePage, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedUpdateRecordsPerPage = useCallback(updateRecordsPerPage, [])

  if (pageState.costChangeItems || pageState.isGetCostChangeItemsLoading) {
    return (
      <>
        <Layout.Body
          className="hesp-costSearch-tableLayout"
          data-testid="cost-change-insights-results"
        >
          <Grid.Container className="hc-pa-normal">
            <Grid.Item className="hesp-general-fullwidth hc-pa-none">
              <span style={{ height: '0px' }} ref={scrollRef} />
              <div className="hesp-general-flex hesp-general-flex-direction-reverse hc-mb-normal">
                <Button
                  className="hesp-general-ghostButton"
                  data-testid="expand-all-items-button"
                  type="ghost"
                  onClick={() => {
                    if (isAtleastOneItemGroupExpanded) {
                      pageDispatch({ type: 'collapse_all_items' })
                    } else {
                      pageDispatch({ type: 'expand_all_items' })
                    }
                  }}
                  disabled={!pageState.costChangeItems?.items?.length}
                >
                  {isAtleastOneItemGroupExpanded
                    ? 'COLLAPSE ALL'
                    : 'EXPAND ALL'}
                </Button>
              </div>
              <Card
                className="hc-pa-sm"
                style={{
                  backgroundColor: 'var(--canvas-background-color--default)',
                }}
              >
                <div
                  style={{ marginRight: '34px', marginLeft: '10px' }}
                  className="hc-fs-lg hesp-general-flex"
                >
                  {headerFields.map((headerField) => {
                    return (
                      <span
                        className="hesp-general-flex-grow hesp-general-centeredText hesp-general-fullwidth"
                        key={`${headerField}-header`}
                      >
                        {headerField}
                      </span>
                    )
                  })}
                </div>
                {pageState.isGetCostChangeItemsLoading ? (
                  <div
                    className="hesp-general-flex hesp-general-justifyCenter hesp-general-alignItemsCenter"
                    style={{
                      height: 'calc(100vh - 176px)',
                      maxHeight: '1044px',
                      minHeight: '500px',
                    }}
                  >
                    <Spinner size="expanded" />
                  </div>
                ) : pageState.costChangeItems?.items?.length ? (
                  <div
                    className="hesp-general-flex"
                    style={{
                      flexDirection: 'column',
                      gap: '6px',
                      height: 'calc(100vh - 220px)',
                      maxHeight: '1000px',
                      minHeight: '456px',
                      overflowY: 'scroll',
                    }}
                  >
                    {pageState?.costChangeItems?.items?.map((item, index) => {
                      return (
                        <CollapsibleItemGrouping
                          key={`collapsible-item-${index}`}
                          itemDetails={item}
                          onExpand={memoizedOnExpand}
                          page={
                            pageState.itemExpandableStates[
                              item[
                                COST_CHANGE_INSIGHTS_ITEM_RESULTS.TCIN
                                  .variableName
                              ]
                            ]?.currentPage
                          }
                          recordsPerPage={
                            pageState.itemExpandableStates[
                              item[
                                COST_CHANGE_INSIGHTS_ITEM_RESULTS.TCIN
                                  .variableName
                              ]
                            ]?.recordsPerPage
                          }
                          updatePage={memoizedUpdatePage}
                          updateRecordsPerPage={memoizedUpdateRecordsPerPage}
                          isExpanded={
                            pageState.itemExpandableStates[
                              item[
                                COST_CHANGE_INSIGHTS_ITEM_RESULTS.TCIN
                                  .variableName
                              ]
                            ]?.isExpanded
                          }
                          submissionRecords={
                            pageState.submissionResults[
                              item[
                                COST_CHANGE_INSIGHTS_ITEM_RESULTS.TCIN
                                  .variableName
                              ]
                            ]
                          }
                        />
                      )
                    })}
                  </div>
                ) : (
                  <div
                    className="hesp-general-flex hesp-general-justifyCenter hesp-general-alignItemsCenter"
                    style={{
                      height: 'calc(100vh - 176px)',
                      maxHeight: '1044px',
                      minHeight: '500px',
                    }}
                  >
                    No items matching the provided filter criteria were found
                  </div>
                )}
                <TablePagination
                  className="hc-mt-sm"
                  dataTestId="cost-change-insights-results-pagination"
                  currentPage={pageState.currentPage}
                  totalRecords={pageState.costChangeItems?.totalResults}
                  recordsPerPage={pageState.recordsPerPage}
                  onRequestNext={() =>
                    pageDispatch({ type: 'go_to_next_page' })
                  }
                  onRequestPrev={() =>
                    pageDispatch({ type: 'go_to_prev_page' })
                  }
                  onRequestFirst={() =>
                    pageDispatch({ type: 'go_to_first_page' })
                  }
                  onRequestLast={() =>
                    pageDispatch({ type: 'go_to_last_page' })
                  }
                  onRequestPage={(pageNum) =>
                    pageDispatch({
                      type: 'update_current_page',
                      value: pageNum,
                    })
                  }
                  onRequestRecordsPerPage={(perPageNum) =>
                    pageDispatch({
                      type: 'update_records_per_page',
                      value: perPageNum,
                    })
                  }
                  recordsPerPageOptions={[10, 25, 50]}
                  hide={pageState.isGetCostChangeItemsLoading}
                />
              </Card>
            </Grid.Item>
          </Grid.Container>
        </Layout.Body>
      </>
    )
  } else {
    return null
  }
}
